import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import type { AbstractControl, UntypedFormGroup } from '@angular/forms';

import { Store } from '@ngrx/store';
import { switchMap, tap } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { OfferPromoteJobBoardsHotspot } from '@core/integrations-types';
import type { DialogComponentI } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DialogService,
  RtStatefulComponent,
} from '@core/user-interface-legacy';

import { CurrentHotspotToken } from '../../../client/integrations-client.module';
import { Destroyable, HttpErrorHandlerService, takeUntilDestroyed } from '../../../common';
import { IntegrationEventActions } from '../../../common/events/events';
import { ErrorMessagesWrapperComponent } from '../../../misc/error-messages-wrapper/error-messages-wrapper.component';
import { HotelprofessionalsApiService } from '../hotelprofessionals-api';
import {
  HotelprofessionalsPostingFormComponent,
  initializePostingFormGroup,
} from '../hotelprofessionals-posting-form/hotelprofessionals-posting-form.component';
import type { EditPostingForm, Posting } from '../types';

type State = {
  posting: Posting | null;
  saving: boolean;
  removing: boolean;
  errors: string[];
  errorFields: { [field: string]: string[] };
};

type DialogData = {
  posting: Posting;
};

const valueIfDirty = (control: AbstractControl) => (control.dirty ? control.value : undefined);

@Component({
  selector: 'rti-hotelprofessionals-edit-posting-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './hotelprofessionals-edit-posting-dialog.component.html',
  styleUrls: ['./hotelprofessionals-edit-posting-dialog.component.less'],
  imports: [
    ButtonComponent,
    ButtonLoadingDirective,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    ErrorMessagesWrapperComponent,
    HotelprofessionalsPostingFormComponent,
    TranslatePipe,
  ],
})
@Destroyable()
export class HotelprofessionalsEditPostingDialogComponent
  extends RtStatefulComponent<State>
  implements DialogComponentI<DialogData, boolean>
{
  private readonly i18n = inject(I18nService);
  private readonly dialogService = inject(DialogService);
  private readonly httpErrorHandlerService = inject(HttpErrorHandlerService);
  private readonly hotelprofessionalsApi = inject(HotelprofessionalsApiService);
  private readonly store = inject(Store);
  private readonly currentHotspot = inject<OfferPromoteJobBoardsHotspot>(CurrentHotspotToken);
  public readonly dialog = inject(DialogRef);

  public postingForm: UntypedFormGroup;

  constructor() {
    const cdRef = inject(ChangeDetectorRef);

    super(cdRef);

    this.createState({
      posting: null,
      saving: false,
      removing: false,
      errors: [],
      errorFields: {},
    });
  }

  public setDialogData(data: DialogData): void {
    this.postingForm = initializePostingFormGroup(data.posting);

    this.postingForm.valueChanges
      .pipe(takeUntilDestroyed(this))
      .subscribe(() => this.cdRef.detectChanges());

    this.setState({ ...this.state, posting: data.posting });
  }

  public save(): void {
    this.setState({ ...this.state, saving: true });

    this.hotelprofessionalsApi
      .patch(`posting/${this.state.posting!.id}`, this.editPostingForm)
      .subscribe(
        (_data: { posting: Posting }) => {
          this.setState({ ...this.state, saving: false });

          this.store.dispatch(
            IntegrationEventActions.HotelprofessionalsPostingEdited({
              payload: { context: { hotspot: this.currentHotspot } },
            }),
          );

          this.close();
        },
        ({ error }) => {
          this.setState({ ...this.state, saving: false, errors: error.error });
        },
      );
  }

  public remove(): void {
    this.dialogService
      .confirm({
        title: this.i18n.translate('rti.job_boards.edit_posting.remove'),
        message: this.i18n.translate('rti.job_boards.edit_posting.remove_confirmation'),
      })
      .pipe(
        filterBoolean,
        tap(() => {
          this.setState({ ...this.state, removing: true });
        }),
        switchMap(() => this.hotelprofessionalsApi.delete(`posting/${this.state.posting!.id}`)),
        tap(
          () => {
            this.setState({ ...this.state, removing: false });

            this.store.dispatch(
              IntegrationEventActions.HotelprofessionalsPostingDeleted({
                payload: { context: { hotspot: this.currentHotspot } },
              }),
            );

            this.dialog.close();
          },
          errorResponse => {
            this.httpErrorHandlerService.default(errorResponse);
            this.setState({ ...this.state, removing: false });
          },
        ),
      )
      .subscribe(() => {
        this.close();
      });
  }

  public close(): void {
    this.dialog.close();
  }

  public get formValid(): boolean {
    return this.postingForm.valid;
  }

  private get editPostingForm(): EditPostingForm {
    return {
      locationId: valueIfDirty(this.postingForm.controls.locationId),
      departments: valueIfDirty(this.postingForm.controls.departments),
      functions: valueIfDirty(this.postingForm.controls.functions),
      employmentType: valueIfDirty(this.postingForm.controls.employmentType),
      weeklyHoursMin: this.postingForm.controls.weeklyHoursMin.dirty
        ? parseInt(this.postingForm.controls.weeklyHoursMin.value, 10)
        : undefined,
      weeklyHoursMax: this.postingForm.controls.weeklyHoursMax.dirty
        ? parseInt(this.postingForm.controls.weeklyHoursMax.value, 10)
        : undefined,
      feature: valueIfDirty(this.postingForm.controls.feature),
      salary: valueIfDirty(this.postingForm.controls.salary),
      education: valueIfDirty(this.postingForm.controls.education),
      experience: valueIfDirty(this.postingForm.controls.experience),
      requiredLanguages: valueIfDirty(this.postingForm.controls.requiredLanguages),
    };
  }
}
